<script setup>
import { computed, useId } from "vue";

// Кнопка «Написать в MAX» с официальным значком мессенджера.
// Одна на весь сайт: блок «Контакты» услуги (Layouts/Address.vue) и сайдбар статьи.
// Цель Метрики задаётся пропом goal.
//
// ⚠ ID ГРАДИЕНТОВ УНИКАЛЬНЫ ДЛЯ КАЖДОЙ КНОПКИ. На странице бывает несколько кнопок,
// и часть скрыта (десктопная на мобиле и наоборот). С одинаковыми id значок в видимой
// кнопке ссылается на градиент из скрытой — и в ряде браузеров рисуется пустым.
// useId() одинаков на сервере и в браузере — без hydration mismatch.
const props = defineProps({
  href: { type: String, default: "" },
  subtitle: { type: String, default: "мессенджер MAX" },
  // compact — одна строка без подписи (узкие места: мобильная карточка юриста)
  compact: { type: Boolean, default: false },
  // flat — второстепенная кнопка дизайн-системы: серая рамка, без тени и увеличения
  flat: { type: Boolean, default: false },
  // iconOnly — только квадратный значок без подписи (мобильная строка контактов)
  iconOnly: { type: Boolean, default: false },
  // Подпись плоской кнопки; по умолчанию «MAX» на телефоне и «Написать в MAX» на десктопе
  label: { type: String, default: "" },
  // Цель Метрики: MAX_Click — контакты услуги, OPEN_MAX_ARTICLE — статья
  goal: { type: String, default: "MAX_Click" },
  // Текст, который кладём в буфер обмена при нажатии: ссылка на профиль MAX не умеет
  // подставлять сообщение (text= есть только у экрана «поделиться» с выбором получателя)
  copyText: { type: String, default: "" },
});
const emit = defineEmits(["copied"]);

function track() {
  if (typeof window !== "undefined" && window.goal) window.goal(props.goal);
  if (props.copyText && typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(props.copyText).then(() => emit("copied", true), () => emit("copied", false));
  } else if (props.copyText) {
    emit("copied", false);
  }
}

// Дописываем протокол, если юрист вставил ссылку без него
const link = computed(() => {
  const m = (props.href ?? "").trim();
  if (!m) return "";
  return /^https?:\/\//i.test(m) ? m : "https://" + m;
});

const uid = useId();
const ids = { a: `maxA-${uid}`, b: `maxB-${uid}`, c: `maxC-${uid}`, d: `maxD-${uid}` };
</script>

<template>
  <a v-if="link" :href="link" target="_blank" rel="noopener nofollow"
    @click="track"
    title="Написать юристу в мессенджере MAX"
    class="inline-flex items-center justify-center bg-white font-medium"
    :class="iconOnly
      ? 'border border-gray-300 bg-gray-50 shadow-sm rounded-xl hover:bg-gray-100 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
      : flat
      ? 'gap-2 text-gray-700 border border-gray-300 py-2.5 px-3 rounded-lg hover:bg-gray-50 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
      : ['gap-2 text-gray-900 border border-gray-200 hover:scale-105 transition-all duration-300', compact ? 'py-2 px-4 rounded-lg shadow' : 'py-3 px-5 rounded-xl shadow-lg']">
    <!-- Официальный значок мессенджера MAX -->
    <svg class="shrink-0 rounded-[6px]" :class="iconOnly ? 'w-8 h-8' : (compact || flat ? 'w-5 h-5' : 'w-6 h-6')"
      viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient :id="ids.b"><stop offset="0" stop-color="#00f"/><stop offset="1" stop-opacity="0"/></linearGradient>
        <linearGradient :id="ids.a"><stop offset="0" stop-color="#4cf"/><stop offset=".662" stop-color="#53e"/><stop offset="1" stop-color="#93d"/></linearGradient>
        <linearGradient :id="ids.c" x1="117.847" x2="1000" y1="760.536" y2="500" gradientUnits="userSpaceOnUse" :href="'#' + ids.a"/>
        <radialGradient :id="ids.d" cx="-87.392" cy="1166.116" r="500" fx="-87.392" fy="1166.116" gradientTransform="rotate(51.356 1551.478 559.3)scale(2.42703433 1)" gradientUnits="userSpaceOnUse" :href="'#' + ids.b"/>
      </defs>
      <rect width="1000" height="1000" :fill="`url(#${ids.c})`" ry="249.681"/>
      <rect width="1000" height="1000" :fill="`url(#${ids.d})`" ry="249.681"/>
      <path fill="#fff" fill-rule="evenodd" d="M508.211 878.328c-75.007 0-109.864-10.95-170.453-54.75-38.325 49.275-159.686 87.783-164.979 21.9 0-49.456-10.95-91.248-23.36-136.873-14.782-56.21-31.572-118.807-31.572-209.508 0-216.626 177.754-379.597 388.357-379.597 210.785 0 375.947 171.001 375.947 381.604.707 207.346-166.595 376.118-373.94 377.224m3.103-571.585c-102.564-5.292-182.499 65.7-200.201 177.024-14.6 92.162 11.315 204.398 33.397 210.238 10.585 2.555 37.23-18.98 53.837-35.587a189.8 189.8 0 0 0 92.71 33.032c106.273 5.112 197.08-75.794 204.215-181.95 4.154-106.382-77.67-196.486-183.958-202.574Z" clip-rule="evenodd"/>
    </svg>
    <span v-if="!iconOnly && flat && label" class="font-semibold text-sm leading-tight whitespace-nowrap">{{ label }}</span>
    <span v-else-if="!iconOnly && flat" class="font-semibold text-sm leading-tight whitespace-nowrap"><span class="hidden lg:inline">Написать в </span>MAX</span>
    <span v-else-if="!iconOnly && compact" class="font-bold text-sm leading-tight whitespace-nowrap">Написать в MAX</span>
    <div v-else-if="!iconOnly" class="flex flex-col items-center">
      <span class="font-bold text-base leading-tight whitespace-nowrap">Написать в MAX</span>
      <span v-if="subtitle" class="text-xs opacity-70 whitespace-nowrap">{{ subtitle }}</span>
    </div>
  </a>
</template>
